import React, { useContext, useState, useEffect, useCallback } from 'react';
import { Input, Button, Typography, Checkbox, Row, Col, Card, Divider, message, Radio, Form, Select, Spin } from 'antd';
import { MailOutlined,LoadingOutlined, LockOutlined, CheckOutlined, UserOutlined, MedicineBoxOutlined, GoogleOutlined, AppleOutlined, IdcardOutlined, PhoneOutlined, EnvironmentOutlined } from '@ant-design/icons';
import auth from '../../../services/auth';
import { notify, debounce } from '../../../utils/helper';
import { useHistory, Link } from 'react-router-dom';
import { SignupWrapper } from './Styled';
import { SpecilityContext } from '../../../context/SpecilityContext';
import { countryCode } from '../../../utils/countryCode';
import { FiPhone } from "react-icons/fi";
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { validateZipCode } from '../../../utils/utils';


const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

/**
 * SignupPage Component with reCAPTCHA v3 Integration
 * 
 * This component implements a two-step signup form with Google reCAPTCHA v3 protection.
 * reCAPTCHA v3 runs in the background and provides a score based on user behavior.
 * 
 * Features:
 * - Two-step form (Personal Info -> Hospital Info)
 * - reCAPTCHA v3 verification on final submission
 * - Error handling for reCAPTCHA failures
 * - Loading states for both form submission and reCAPTCHA verification
 * - Visual reCAPTCHA badge for user awareness
 */
const SignupPage = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [recaptchaLoading, setRecaptchaLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [form] = Form.useForm();
  const [formHeight, setFormHeight] = useState('auto');
  const [illustration, setIllustration] = useState(0);
  const [formData, setFormData] = useState({});
  const { specialtyOptions } = useContext(SpecilityContext);
  const [zip, setZip] = useState('');
  const [message, setMessage] = useState('');
  const [zipData, setZipData] = useState(null);
  const [zipLoading, setZipLoading] = useState(false);
  const plan = new URLSearchParams(window.location.search).get('plan');
  const { executeRecaptcha } = useGoogleReCaptcha();
  // Change illustration every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setIllustration((prev) => (prev + 1) % 3);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const handleSubmit = async (values) => {
    if (step < 2) {
      // Store current step values
      setFormData(prev => ({
        ...prev,
        ...values
      }));

      setStep(step + 1);
      setTimeout(() => {
        const formElement = document.getElementById('signup-form');
        if (formElement) {
          setFormHeight(`${formElement.offsetHeight}px`);
        }
      }, 0);
    } else {
      setLoading(true);

      try {
        // Execute reCAPTCHA verification
        if (!executeRecaptcha) {
          notify('error', 'reCAPTCHA not available. Please refresh the page and try again.');
          setLoading(false);
          return;
        }

        setRecaptchaLoading(true);
        let recaptchaToken;
        try {
          // Execute reCAPTCHA with 'signup' action - this helps Google understand the context
          // and provide appropriate scoring based on the action being performed
          recaptchaToken = await executeRecaptcha('signup');
          // console.log('reCAPTCHA token:', recaptchaToken);
          
          if (!recaptchaToken) {
            notify('error', 'reCAPTCHA verification failed. Please try again.');
            setLoading(false);
            setRecaptchaLoading(false);
            return;
          }
        } catch (recaptchaError) {
          // console.error('reCAPTCHA error:', recaptchaError);
          notify('error', 'reCAPTCHA verification failed. Please try again.');
          setLoading(false);
          setRecaptchaLoading(false);
          return;
        } finally {
          setRecaptchaLoading(false);
        }

        // Combine stored values with current step values
        const finalValues = {
          ...formData,
          ...values
        };

        // Create the exact payload required
        const payload = {
          name: finalValues.name,
          email: finalValues.email,
          hospitalName: finalValues.hospitalName,
          // mobile: finalValues.mobile,
          description: finalValues.description,
          zip: zip,
          plan: plan || 'standard',
          specialty: finalValues?.specialty || [],
          reCaptchaToken: recaptchaToken, // Add reCAPTCHA token to payload
          timeZone: zipData,
        };

        // console.log('Signup payload:', payload);
        const response = await auth.signUp(payload);
        // console.log('Signup response:', response);
        if(response?.data?.exist) {
          return notify('error', 'A user with this email already exists. Please try signing up with a different email address.')
        }
        if (response.status === 200 || response.status === 201) {
          window.location.href = response?.data?.session?.url;
          // notify('success', 'Account created successfully!');
          // You might want to redirect to login or dashboard here
          // history.push('/login');
        }
      } catch (error) {
        // console.error('Signup error:', error);
        notify('error', error.response?.data?.message || 'Failed to create account. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleBack = () => {
    setStep(step - 1);
    // Restore previous step values
    form.setFieldsValue(formData);
    setMessage('');
    setZipData(null);
  };

  const handleGoogleSignup = () => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      message.success('Google signup initiated!');
      setLoading(false);
    }, 1500);
  };

  const handleZipChange = (e) => {
    const value = e.target.value;
    setZip(value);
    if (value.length >= 3) {
      validateDebounced(value, "US");
    } else {
      setMessage('');
      setZipData(null);
    }
  };

  const validateDebounced = useCallback(
    debounce(async (zipVal, countryCodeVal) => {
      setZipLoading(true);
      try {
        const msg = await validateZipCode(zipVal, countryCodeVal);
        setMessage(msg.message);
        setZipData(msg.data);
      } catch (err) {
        setMessage('Error validating ZIP');
        setZipLoading(false)
      } finally {
        setZipLoading(false)
      }
    }, 600),
    []
  );

  // const prefixSelector = (
  //   <Form.Item name={["mobile", "countryCode"]} noStyle initialValue="+1">
  //     <Select 
  //       style={{ width: 75 }}
  //       showSearch
  //       dropdownStyle={{ minWidth: 200 ,maxHeight: 200, overflowY: 'auto', overflowX: 'hidden' }}
  //       listHeight={160}
  //       optionLabelProp="label"
  //       filterOption={(input, option) =>
  //       option?.label?.toLowerCase().includes(input.toLowerCase())
  //       || option?.name?.toLowerCase().includes(input.toLowerCase())
  //       }
  //     >
  //       {countryCode.map((item) => (
  //         <Option 
  //           key={item.dial_code} 
  //           value={`+${item.dial_code}`}
  //           name={item.name}
  //           label={`+${item.dial_code}`}
  //         >
  //           {item.name} (+{item.dial_code})
  //         </Option>
  //       ))}
  //     </Select>
  //   </Form.Item>
  // );

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <>
            <Form.Item
              name="name"
              rules={[{ required: true, message: 'Please enter your name' }]}
              initialValue={formData.name}
            >
              <Input
                prefix={<UserOutlined className="site-form-item-icon" />}
                placeholder="Full Name"
                size="large"
                className="d-flex justify-content-center"
                style={{ height: 50, borderRadius: 8 }}
              />
            </Form.Item>

            <Form.Item
              name="email"
              rules={[
                { required: true, message: 'Please enter your email' },
                { type: 'email', message: 'Please enter a valid email' }
              ]}
              initialValue={formData.email}
            >
              <Input
                prefix={<MailOutlined className="site-form-item-icon" />}
                placeholder="Email Address"
                size="large"
                className="d-flex justify-content-center"
                style={{ height: 50, borderRadius: 8 }}
              />
            </Form.Item>

            {/* <Form.Item
              name={["mobile", "number"]}
              rules={[
                { required: true, message: 'Please enter your phone number' },
                { pattern: /^\d{10}$/, message: 'Phone number must be exactly 10 digits'},
              ]}
              initialValue={formData.phone}
            >
              <Input
                maxLength={10}
                prefix={<FiPhone className="site-form-item-icon" />}
                addonBefore={prefixSelector}
                placeholder="Phone Number"
                size="large"
                className="d-flex justify-content-center"
                style={{ height: 50, borderRadius: 8 }}
                onKeyPress={(e) => {
                  if (!/[0-9]/.test(e.key)) {
                    e.preventDefault(); // block non-numeric input
                  }
                }}
              />
            </Form.Item> */}

            <Form.Item
              name="agreement"
              valuePropName="checked"
              rules={[
                {
                  validator: (_, value) =>
                    value ? Promise.resolve() : Promise.reject(new Error('You must accept the terms and conditions')),
                },
              ]}
              style={{ marginTop: 24 }}
            >
              <Checkbox>
                By signing up, you agree to our <a target='_blank' href="https://www.meducate.ai/terms-of-service"> Terms of Service</a> and <a target='_blank' href="https://www.meducate.ai/privacy-policy">Privacy Policy</a>  and consent to receive SMS and email communications from us related to your account and educational content.

              </Checkbox>
            </Form.Item>

            {/* <Form.Item
              name="password"
              rules={[
                { required: true, message: 'Please enter your password' },
                { min: 8, message: 'Password must be at least 8 characters' }
              ]}
              initialValue={formData.password}
            >
              <Input.Password
                prefix={<LockOutlined className="site-form-item-icon" />}
                placeholder="Password"
                size="large"
                style={{ height: 50, borderRadius: 8 }}
              />
            </Form.Item> */}
          </>
        );
      case 2:
        return (
          <>
            <Form.Item
              name="hospitalName"
              rules={[{ required: true, message: 'Please enter hospital name' }]}
              initialValue={formData.hospitalName}
            >
              <Input
                prefix={<MedicineBoxOutlined className="site-form-item-icon" />}
                placeholder="Group Name"
                size="large"
                style={{ height: 50, borderRadius: 8 }}
              />
            </Form.Item>

            <Form.Item
              name="description"
              rules={[{ required: true, message: 'Please enter hospital description' }]}
              initialValue={formData.description}
            >
              <Input.TextArea
                placeholder="Group Description"
                size="large"
                style={{ borderRadius: 8 }}
                rows={4}
              />
            </Form.Item>

            <Form.Item
              name="zip"
              rules={[() => ({
                validator(_, value) {
                  if (!zip) {
                    return Promise.reject(new Error('Please enter the zip code!'));
                  }
                  return Promise.resolve();
                },
              }),]}
              initialValue={formData.location}
            >
              <Input
                placeholder="Zip Code"
                size="large"
                onChange={handleZipChange}
                style={{ height: 50, borderRadius: 8 }}
              />
              {zipLoading ? (
                <Spin indicator={<LoadingOutlined spin />} className="customZipSpin"/>
                ):(<span className="customZipSpin font-12 text-primary">{zipData && zipData}</span>)}
              {message && (
                <p className="m-0" style={{color: zipData?"green":"red", fontSize: "13px" }}>{message}</p>
              )}
            </Form.Item>

            {plan !== 'standard' && <Form.Item
              name="specialty"
              label="Medical Specialty"
              rules={[{ required: true, message: "Please select a specialty!" },            
                {
                  validator: (_, value) => {
                    if (!value || value.length <= 2) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Maximum 2 specialties are allowed'));
                  },
                },
              ]}
              
            >
              <Select
                mode="multiple"
                getPopupContainer={(triggerNode) => triggerNode.parentNode}
                placeholder="Select medical specialty"
                showSearch
                style={{ width: '100%' }}
                dropdownStyle={{ maxHeight: 200, overflowY: 'auto', overflowX: 'hidden' }}
                options={specialtyOptions}
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>}




          </>
        );
      default:
        return null;
    }
  };

  const illustrations = [
    // First illustration - Doctor with patient
    (
      <div className="animate-float" style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
        animation: 'fadeIn 0.5s ease-in-out'
      }}>
        <div style={{
          position: 'absolute',
          width: '120%',
          height: '120%',
          top: '-10%',
          left: '-10%',
          background: 'radial-gradient(circle at center, rgba(102, 0, 255, 0.2) 0%, rgba(51, 102, 255, 0.1) 50%, transparent 70%)',
          zIndex: 0
        }} />

        <div style={{
          position: 'relative',
          zIndex: 1,
          textAlign: 'center'
        }}>
          <div className="animate-pulse" style={{
            width: 200,
            height: 200,
            background: 'rgba(51, 102, 255, 0.1)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 32px',
          }}>
            <div style={{
              width: 160,
              height: 160,
              background: 'linear-gradient(135deg, #3366ff 0%, #6600ff 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 12px 30px rgba(51, 102, 255, 0.3)',
            }}>
              <MedicineBoxOutlined style={{ fontSize: 80, color: 'white' }} />
            </div>
          </div>

          <Title level={2} style={{ color: 'white', marginBottom: 16 }}>
            Join Meducate Today
          </Title>
          <Paragraph style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: 16, maxWidth: 320, margin: '0 auto' }}>
            Connect with healthcare professionals and manage your medical needs in one secure platform
          </Paragraph>
        </div>

        {/* Floating elements */}
        <div className="floating-pill" style={{
          position: 'absolute',
          width: 60,
          height: 30,
          borderRadius: '50px',
          background: 'rgba(255, 255, 255, 0.1)',
          bottom: '30%',
          left: '15%',
          animation: 'float 6s ease-in-out infinite 1s',
        }} />

        <div className="floating-circle" style={{
          position: 'absolute',
          width: 40,
          height: 40,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.1)',
          top: '20%',
          right: '20%',
          animation: 'float 8s ease-in-out infinite',
        }} />
      </div>
    ),

    // Second illustration - Medical equipment
    (
      <div className="animate-float" style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
        animation: 'fadeIn 0.5s ease-in-out'
      }}>
        <div style={{
          position: 'absolute',
          width: '120%',
          height: '120%',
          top: '-10%',
          left: '-10%',
          background: 'radial-gradient(circle at bottom right, rgba(102, 0, 255, 0.2) 0%, rgba(51, 102, 255, 0.1) 50%, transparent 70%)',
          zIndex: 0
        }} />

        <div style={{
          position: 'relative',
          zIndex: 1,
          textAlign: 'center'
        }}>
          <div style={{
            margin: '0 auto 32px',
            position: 'relative',
            width: 200,
            height: 200,
          }}>
            {/* Stylized heartbeat line */}
            <div style={{
              position: 'absolute',
              width: '100%',
              height: 4,
              background: 'rgba(255, 255, 255, 0.2)',
              top: '50%',
              left: 0,
              zIndex: 0
            }} />

            <div className="heartbeat-line" style={{
              position: 'absolute',
              width: '100%',
              height: 60,
              top: '50%',
              left: 0,
              marginTop: -30,
              zIndex: 1
            }}>
              <svg width="100%" height="100%" viewBox="0 0 200 60">
                <path
                  d="M0,30 L40,30 L50,10 L60,50 L70,30 L80,30 L90,20 L100,40 L110,30 L200,30"
                  fill="none"
                  stroke="#3366ff"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>
            </div>

            <div style={{
              position: 'absolute',
              width: 100,
              height: 100,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #6600ff 0%, #3366ff 100%)',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 12px 30px rgba(51, 102, 255, 0.3)',
              zIndex: 2
            }}>
              <div style={{ fontSize: 50, color: 'white' }}>+</div>
            </div>
          </div>

          <Title level={2} style={{ color: 'white', marginBottom: 16 }}>
            Healthcare Made Simple
          </Title>
          <Paragraph style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: 16, maxWidth: 320, margin: '0 auto' }}>
            Schedule appointments, access medical records, and connect with your healthcare team securely
          </Paragraph>
        </div>

        {/* Floating medical icons */}
        <div className="floating-icon" style={{
          position: 'absolute',
          width: 50,
          height: 50,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.08)',
          top: '15%',
          left: '20%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: 'float 7s ease-in-out infinite 0.5s',
          color: 'white',
          fontSize: 24
        }}>
          <span>💊</span>
        </div>

        <div className="floating-icon" style={{
          position: 'absolute',
          width: 40,
          height: 40,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.08)',
          bottom: '20%',
          right: '25%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: 'float 6s ease-in-out infinite 1s',
          color: 'white',
          fontSize: 20
        }}>
          <span>🩺</span>
        </div>
      </div>
    ),

    // Third illustration - Digital health
    (
      <div className="animate-float" style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
        animation: 'fadeIn 0.5s ease-in-out'
      }}>
        <div style={{
          position: 'absolute',
          width: '120%',
          height: '120%',
          top: '-10%',
          left: '-10%',
          background: 'radial-gradient(circle at top left, rgba(102, 0, 255, 0.2) 0%, rgba(51, 102, 255, 0.1) 50%, transparent 70%)',
          zIndex: 0
        }} />

        <div style={{
          position: 'relative',
          zIndex: 1,
          textAlign: 'center'
        }}>
          <div style={{
            margin: '0 auto 32px',
            position: 'relative',
            width: 200,
            height: 200,
          }}>
            {/* Mobile device outline */}
            <div style={{
              position: 'absolute',
              width: 120,
              height: 200,
              borderRadius: 20,
              border: '4px solid rgba(255, 255, 255, 0.3)',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1
            }} />

            {/* App interface */}
            <div style={{
              position: 'absolute',
              width: 100,
              height: 180,
              borderRadius: 12,
              background: 'linear-gradient(135deg, #3366ff 0%, #6600ff 100%)',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '20px 10px',
              boxShadow: '0 12px 30px rgba(51, 102, 255, 0.3)',
            }}>
              <div style={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 15
              }}>
                <MedicineBoxOutlined style={{ fontSize: 24, color: '#3366ff' }} />
              </div>

              <div style={{
                width: '80%',
                height: 8,
                background: 'rgba(255, 255, 255, 0.4)',
                borderRadius: 4,
                marginBottom: 10
              }} />

              <div style={{
                width: '60%',
                height: 8,
                background: 'rgba(255, 255, 255, 0.4)',
                borderRadius: 4,
                marginBottom: 20
              }} />

              <div style={{
                width: '70%',
                height: 30,
                background: 'rgba(255, 255, 255, 0.2)',
                borderRadius: 6,
                marginBottom: 15
              }} />

              <div style={{
                width: '70%',
                height: 30,
                background: 'rgba(255, 255, 255, 0.2)',
                borderRadius: 6,
              }} />
            </div>

            {/* Decorative rings */}
            <div style={{
              position: 'absolute',
              width: 240,
              height: 240,
              borderRadius: '50%',
              border: '2px dashed rgba(255, 255, 255, 0.2)',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 0,
              animation: 'rotate 60s linear infinite'
            }} />
          </div>

          <Title level={2} style={{ color: 'white', marginBottom: 16 }}>
            Digital Healthcare
          </Title>
          <Paragraph style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: 16, maxWidth: 320, margin: '0 auto' }}>
            Experience modern healthcare with our innovative digital solutions
          </Paragraph>
        </div>

        {/* Floating elements */}
        <div className="floating-square" style={{
          position: 'absolute',
          width: 30,
          height: 30,
          borderRadius: 8,
          background: 'rgba(255, 255, 255, 0.1)',
          top: '30%',
          right: '20%',
          animation: 'float 8s ease-in-out infinite 0.3s',
          transform: 'rotate(30deg)'
        }} />

        <div className="floating-circle" style={{
          position: 'absolute',
          width: 20,
          height: 20,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.1)',
          bottom: '25%',
          left: '30%',
          animation: 'float 6s ease-in-out infinite 1.5s',
        }} />
      </div>
    )
  ];

  // { name, email, password, hospitalName, description, location }

  return (
    <SignupWrapper>
      {/* Left side - Animated illustrations (40%) */}
      <div style={{
        flex: '0 0 40%',
        background: 'linear-gradient(135deg, #111122 0%, #1a1a40 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {illustrations[illustration]}
      </div>

      {/* Right side - Signup form (60%) */}
      <div style={{
        flex: '0 0 60%',
        background: 'white',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px'
      }}>
        <div style={{ maxWidth: 480, width: '100%' }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <Title level={2} style={{ fontSize: 32, marginBottom: 8 }}>
              {step === 1
                ? 'Create an Account'
                : 'Hospital Information'}
            </Title>
            <Text type="secondary" style={{ fontSize: 16 }}>
              {step === 1
                ? 'Enter your basic information'
                : 'Enter your hospital details'}
            </Text>
          </div>

          {/* Step indicators */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: 32,
            position: 'relative',
            maxWidth: '100%'
          }}>
            {[1, 2].map((i) => (
              <div
                key={i}
                style={{
                  zIndex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  position: 'relative',
                  width: '50%'
                }}
              >
                <div style={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: i <= step ? 'linear-gradient(135deg, #3366ff 0%, #6600ff 100%)' : 'white',
                  border: i <= step ? 'none' : '2px solid #e8e8e8',
                  color: i <= step ? 'white' : 'rgba(0, 0, 0, 0.45)',
                  fontWeight: 'bold',
                  transition: 'all 0.3s ease',
                  marginBottom: 8
                }}>
                  {i < step ? <CheckOutlined /> : i}
                </div>
                <Text
                  style={{
                    fontSize: 12,
                    color: i <= step ? 'rgba(0, 0, 0, 0.85)' : 'rgba(0, 0, 0, 0.45)',
                    transition: 'color 0.3s ease'
                  }}
                >
                  {i === 1 ? 'Personal Information' : 'Hospital Information'}
                </Text>
              </div>
            ))}

            {/* Progress line */}
            <div style={{
              position: 'absolute',
              top: 20,
              left: '25%',
              right: '25%',
              height: 2,
              background: '#e8e8e8',
              zIndex: 0
            }} />

            <div style={{
              position: 'absolute',
              top: 20,
              left: '25%',
              width: step === 1 ? '0%' : '50%',
              height: 2,
              background: 'linear-gradient(to right, #3366ff, #6600ff)',
              zIndex: 0,
              transition: 'width 0.5s ease'
            }} />
          </div>

          {/* Form */}
          <div style={{ minHeight: formHeight, transition: 'min-height 0.3s ease' }}>
            <Form
              id="signup-form"
              form={form}
              onFinish={handleSubmit}
              layout="vertical"
              requiredMark={false}
              scrollToFirstError
            >
              {renderStepContent()}

              <Form.Item style={{ marginTop: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  {step > 1 && (
                    <Button
                      size="large"
                      onClick={handleBack}
                      style={{
                        height: 50,
                        minWidth: 120,
                        borderRadius: 8
                      }}
                    >
                      Back
                    </Button>
                  )}

                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    loading={loading || recaptchaLoading}
                    style={{
                      height: 50,
                      backgroundImage: 'linear-gradient(135deg, #3366ff 0%, #6600ff 100%)',
                      border: 'none',
                      borderRadius: 8,
                      fontSize: 16,
                      fontWeight: 500,
                      marginLeft: step > 1 ? 'auto' : 0,
                      minWidth: 120
                    }}

                  >
                    {step < 2 ? 'Continue' : (recaptchaLoading ? 'Verifying...' : 'Create Account')}
                  </Button>
                </div>
              </Form.Item>
            </Form>
          </div>

          {step === 1 && (
            <>
              {/* <Divider style={{ margin: '32px 0 24px' }}>
                <Text type="secondary">OR</Text>
              </Divider> */}
              {/*               
              <div style={{ display: 'flex', gap: 16 }}>
                <Button
                  block
                  size="large"
                  icon={<GoogleOutlined />}
                  onClick={handleGoogleSignup}
                  style={{ 
                    height: 50,
                    borderRadius: 8,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8
                  }}
                >
                  Sign up with Google
                </Button>
                
                <Button
                  block
                  size="large"
                  icon={<AppleOutlined />}
                  style={{ 
                    height: 50,
                    borderRadius: 8,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8
                  }}
                >
                  Sign up with Apple
                </Button>
              </div> */}
            </>
          )}

          <div style={{ textAlign: 'center', marginTop: 24 }}>
            <Text type="secondary">
              Already have an account? <Link to="/login" style={{ color: '#3366ff', fontWeight: 500 }}>Sign In</Link>
            </Text>
          </div>
          
          {/* reCAPTCHA Badge */}
          <div className="recaptcha-badge">
            <div className="recaptcha-badge-icon">
              ✓
            </div>
            <span>Protected by reCAPTCHA</span>
          </div>
        </div>
      </div>

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes float {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-15px); }
          100% { transform: translateY(0px); }
        }
        
        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); }
        }
        
        @keyframes rotate {
          from { transform: translate(-50%, -50%) rotate(0deg); }
          to { transform: translate(-50%, -50%) rotate(360deg); }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        .animate-pulse {
          animation: pulse 3s ease-in-out infinite;
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        
        .heartbeat-line {
          animation: pulse 2s ease-in-out infinite;
        }
        
        .role-card:hover {
          border-color: #3366ff !important;
          box-shadow: 0 4px 12px rgba(51, 102, 255, 0.15);
        }
      `}</style>
    </SignupWrapper>
  );
};

export default SignupPage;